import React, {useEffect, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {Space} from 'antd';
import {
  StyledSettingPageContainer,
  StyledTabComponentContainer,
  StyledTabs
} from './StyledSettingsPage';
import PageContainer from '../../components/PageContainer';
import SpinSmall from '../../components/SpinSmall';
import Budgets from '../../components/pages/SettingsPage/tabs/Budgets';
import CompanyInfo from '../../components/pages/SettingsPage/tabs/CompanyInfo';
import InvoiceRetrieval from '../../components/pages/SettingsPage/tabs/InvoiceRetrieval';
import Policies from '../../components/pages/SettingsPage/tabs/Policies';
import TagsManagement from '../../components/pages/SettingsPage/tabs/TagsManagement';
import Team from '../../components/pages/SettingsPage/tabs/Team';
import UsageMonitoring from '../../components/pages/SettingsPage/tabs/UsageMonitoring';
import LegalDocuments from '../../components/pages/SettingsPage/tabs/LegalDocuments';
import LegalInformation from '../../components/pages/SettingsPage/tabs/LegalInformation';
import BalancePageHeader from '../../components/pages/TransactionsPage/BalancePageHeader/BalancePageHeader';
import PageDocumentDetails from '../../components/PageDocumentDetails';
import BillingInformation from '../../components/pages/SettingsPage/tabs/BillingInformation';
import BillingDetailsInformation from '../../components/pages/SettingsPage/tabs/BillingDetailsInformation';
import {billingActions} from '../../state/actions/billing.actions';
import {companyActions} from '../../state/actions/company.actions';
import {locationHelpers, objectHelpers, systemHelpers, transactionsHelpers} from '../../utils/helpers';
import {availableModulesConstants, companyFeaturesConstants, rewardStatusesConstants} from '../../constants';
import {useFeatureAvailable, useHasAccess} from '../../hooks';

const isAvailableSSOModule = systemHelpers.checkIfAvailableFeature(availableModulesConstants.SSO);

const defaultRewardsTotals = {
  alreadyClaimed: 0,
  toBeClaimed: 0,
  total: 0,
  loaded: false
}

const SettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [t] = useTranslation(['settings', 'main']);
  const [activeTabKey, setActiveTabKey] = useState(locationHelpers.getHash(location) || 'overview');
  const [loading, setLoading] = useState(false);
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [billingInformation, setBillingInformation] = useState({
    initialLoaded: false,
    data: [],
    loading: true,
    pagination: null
  });
  const [rewards, setRewards] = useState({
    data: [],
    loaded: false
  });
  const [rewardsTotals, setRewardsTotals] = useState(defaultRewardsTotals);
  const hasEditAccess = useHasAccess('PUT_UPDATE_COMPANY_SETTING');
  const hasEditTagsAccess = useHasAccess('POST_COMPANY_ADD_TAGS');

  const isBudgetsAvailable = useFeatureAvailable(companyFeaturesConstants.BUDGETS_PO_AVAILABLE);

  const dispatch = useDispatch();
  const companyId = useSelector(state => state?.company?.id);

  const tabTrans = (key) => t(`tabs.${key}.title`);

  useEffect(() => {
    if (companyId) {
      setLoading(true);
      dispatch(companyActions.getCompanySettings(
        (settings) => {
          setInvoiceEmail(transactionsHelpers.getInvoiceEmail(settings));
          setLoading(false);
        },
        () => {
          invoiceEmail && setInvoiceEmail('');
          setLoading(false);
        }
      ));
    }
  }, [dispatch, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const billingData = useMemo(() => {
    return billingInformation.data.map(billingItem => {
      let currentDate = dayjs(billingItem.created_date * 1000);
      const rewardObj = rewards.data.find(j => {
        const objData = dayjs().month(j.month - 1).year(j.year);
        return objData.year() === currentDate.year() && objData.month() === currentDate.month();
      });
      return {
        ...billingItem,
        cashback: rewardObj ? rewardObj.total : undefined
      }
    });
  }, [billingInformation.data, rewards.data]);

  const handleLoadBillingTransfers = () => {
    let cursor = objectHelpers.getObjProp(billingInformation.pagination, 'cursor') || undefined;
    if (billingInformation.initialLoaded && cursor === undefined) return;

    setBillingInformation({
      ...billingInformation,
      loading: true
    });

    dispatch(billingActions.getTransfersList({
      query: { cursor },
      successCallback: (data) => {
        setBillingInformation({
          ...billingInformation,
          data: [...billingInformation.data, ...(data.results || [])],
          initialLoaded: true,
          loading: false,
          pagination: data.pagination
        });
      },
      errorCallback: () => {
        setBillingInformation({
          ...billingInformation,
          initialLoaded: true,
          loading: false
        });
      }
    }));
  }

  const overviewTabContent = (
    <Space direction='vertical' size='middle'>
      <CompanyInfo edit={hasEditAccess} />
      <InvoiceRetrieval email={invoiceEmail} />
    </Space>
  );

  const tabs = [
    {
      children: isAvailableSSOModule ? (
        <StyledTabComponentContainer
          align='start'
          size='large'
        >
          {overviewTabContent}
          <UsageMonitoring edit={hasEditAccess} />
        </StyledTabComponentContainer>
      ) : overviewTabContent,
      key: 'overview',
      label: tabTrans('overview')
    },
    {
      children: <Team />,
      key: 'team',
      label: tabTrans('team'),
    },
    {
      children: <Policies />,
      key: 'expenses',
      label: tabTrans('expenses')
    },
    isBudgetsAvailable && {
      children: <Budgets />,
      key: 'procurement',
      label: tabTrans('procurement')
    },
    {
      children: <TagsManagement edit={hasEditTagsAccess} />,
      key: 'properties',
      label: tabTrans('properties')
    },
    {
      children: (
        <StyledTabComponentContainer
          align='start'
          size='large'
        >
          <LegalInformation />
          <LegalDocuments />
        </StyledTabComponentContainer>
      ),
      key: 'legal',
      label: tabTrans('legal')
    },
    {
      children: (
        <StyledTabComponentContainer
          align='start'
          size='large'
        >
          <BillingInformation
            data={billingData}
            loading={billingInformation.loading}
            pagination={billingInformation.pagination}
            handleLoadMore={handleLoadBillingTransfers}
          />
          <SpinSmall spinning={!rewardsTotals.loaded}>
            <BillingDetailsInformation
              handleClaimComplete={() => handleLoadRewards(true)}
              totals={rewardsTotals}
            />
          </SpinSmall>
        </StyledTabComponentContainer>
      ),
      key: 'billing',
      label: tabTrans('billing')
    }
  ].filter(Boolean);

  const handleLoadRewards = (forceUpdate = false) => {
    if (forceUpdate || !rewards.loaded) {
      dispatch(billingActions.getRewardsList({
        successCallback: (data) => setRewards({data, loaded: true}),
        errorCallback: () => setRewards({data: [], loaded: true})
      }));
    }
    if (forceUpdate || !rewardsTotals.loaded) {
      dispatch(billingActions.getRewardsTotals({
        successCallback: (data) => {
          const totals = data?.totals || {};
          const getTotalValue = (key) => totals?.[key] || 0;
          const ready = getTotalValue(rewardStatusesConstants.READY);
          const claimed = getTotalValue(rewardStatusesConstants.CLAIMED);
          const pending = getTotalValue(rewardStatusesConstants.PENDING);
          const paid = getTotalValue(rewardStatusesConstants.PAID);
          setRewardsTotals({
            alreadyClaimed: paid + claimed,
            toBeClaimed: ready,
            total: ready + pending + paid + claimed,
            loaded: true
          });
        },
        errorCallback: () => setRewardsTotals({...defaultRewardsTotals, loaded: true})
      }));
    }
  }

  const loadBillingDetails = () => {
    handleLoadBillingTransfers();
    handleLoadRewards();
  }

  useEffect(() => {
    const methods = {
      'billing': loadBillingDetails,
    }
    if (methods.hasOwnProperty(activeTabKey)) methods[activeTabKey]();
  }, [activeTabKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeTab = (key) => {
    setActiveTabKey(key);
    navigate(`${location.pathname}#${key}`);
  }

  return (
    <PageContainer>
      <PageDocumentDetails title={t('main:pageTitles.settings')} />
      <BalancePageHeader breadcrumbs={[ {title: t('title')} ]} />
      <SpinSmall spinning={loading}>
        <StyledSettingPageContainer>
          <StyledTabs
            activeKey={activeTabKey}
            type='card'
            items={tabs}
            onChange={handleChangeTab}
          />
        </StyledSettingPageContainer>
      </SpinSmall>
    </PageContainer>
  );
}

export default SettingsPage;
